// @flow
import { replace } from 'connected-react-router';

import Constants from '../constants';
import { getAnswer, getAnswerCount, getAnswers, putAnswer } from '../rest/answer';
import { postAnswer } from '../rest/content-micros';
import { deleteAnswer } from '../rest/content-micros';
import { getParentID } from '../rest/content';
import { restActionCreator } from './index';
import { ACTION_SUCCESS } from '../constants/action-status';
import type { Dispatch, ActionResult, GetState } from './types';
import { AnswerRecord } from '../records';
import { getCursorQueryParam } from '../utils';
import {
	answerCountTransformer,
	answersTransformer,
	answerTransformer,
} from '../transformers/answer';
import { answersListSchema, answersSchema } from '../schemas/entities';
import { sendTrackEvent } from '../utils/analytics';
import { ANSWER_DELETE } from '../constants/cq-analytics';

export const createAnswer =
	(title: string, body: string, parentId: string, spaceKey: string) => (dispatch: Dispatch) =>
		dispatch(
			restActionCreator(
				() => postAnswer(title, body, parentId, spaceKey),
				Constants.CREATE_ANSWER,
				// // Code may be moved to the components - https://jira.atlassian.com/browse/CONFDEV-55607
			),
		).then((actionResult: ActionResult) => fetchAnswerIfSuccessful(dispatch, actionResult));

export const updateAnswer =
	(body: string, content: AnswerRecord, parentId: string, spaceKey: string) =>
	(dispatch: Dispatch) =>
		dispatch(
			restActionCreator(
				() => putAnswer(body, content, parentId, spaceKey),
				Constants.UPDATE_ANSWER,

				// // Code may be moved to the components - https://jira.atlassian.com/browse/CONFDEV-55607
			),
		).then((actionResult: ActionResult) => fetchAnswerIfSuccessful(dispatch, actionResult));

export const removeAnswer = (contentId: string, parentId: string) => (dispatch: Dispatch) => {
	return dispatch(
		restActionCreator(
			() => deleteAnswer(contentId, parentId),
			Constants.DELETE_ANSWER,
			undefined,
			undefined,
			{
				entityId: contentId,
			},
		),
	).then((actionResult: ActionResult) => {
		if (actionResult.type.includes(ACTION_SUCCESS)) {
			sendTrackEvent(ANSWER_DELETE);
		}
	});
};

export const answerForwardToQuestion =
	(answerId: string, commentId: ?string) => (dispatch: Dispatch) =>
		dispatch(
			restActionCreator(() => getParentID(answerId), Constants.FORWARD_TO_QUESTION_FROM_ANSWER_ID),
		).then((actionResult: ActionResult) => {
			// Code may be moved to the components - https://jira.atlassian.com/browse/CONFDEV-55607
			if (actionResult.type.includes(ACTION_SUCCESS)) {
				if (commentId) {
					dispatch(
						replace(
							`/questions/${actionResult.payload.results}/answer/${answerId}/comment/${commentId}`,
						),
					);
				} else {
					dispatch(replace(`/questions/${actionResult.payload.results}/answer/${answerId}`));
				}
			} else {
				dispatch(replace('/questions'));
			}
		});

export const fetchAnswers =
	(id: string, page: number = 0) =>
	(dispatch: Dispatch, getState: GetState) => {
		const state = getState();
		const nextLink = state.views.answers.getIn(['_links', 'next'], '');
		const cursor = getCursorQueryParam(nextLink);

		dispatch(
			restActionCreator(
				() => getAnswers(id, page, cursor),
				Constants.FETCH_ANSWERS,
				answersTransformer,
				answersListSchema,
			),
		);
	};

export const fetchAnswerCount = (id: string) => (dispatch: Dispatch) =>
	dispatch(
		restActionCreator(
			() => getAnswerCount(id),
			Constants.FETCH_ANSWER_COUNT,
			answerCountTransformer,
		),
	);

export const fetchAnswer = (id: string) => (dispatch: Dispatch) =>
	dispatch(
		restActionCreator(
			() => getAnswer(id),
			Constants.FETCH_ANSWER,
			answerTransformer,
			answersSchema,
		),
	);

export const fetchAnswersIfSuccessful = (
	dispatch: Dispatch,
	actionResult: ActionResult,
	questionId: string,
) => {
	if (actionResult.type.includes(ACTION_SUCCESS)) {
		dispatch(fetchAnswers(questionId));
	}
};

export const fetchAnswerIfSuccessful = (dispatch: Dispatch, actionResult: ActionResult) => {
	if (actionResult.type.includes(ACTION_SUCCESS)) {
		dispatch(fetchAnswer(actionResult.payload.results.id));
	}
};
